/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { createStyles, IconButton, InputAdornment, makeStyles } from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import React from 'react';
import styled from 'styled-components';
import { DSClickAwayListener } from '../../../mui/ClickAwayListener';
import { DSPopperProps } from '../../../mui/Popper';
import {
    InputDropdownListOnSelectItemHandler,
    InputDropdownListOptions,
    InputDropdownListProps,
    InputDropdownList,
} from '../InputDropdown';
import { ChipFieldProps as _ChipFieldProps, ChipFieldRemoveHandler, ChipFieldValueArray, ChipField } from './ChipField';
import { useRemoveIndexFromArray } from './useRemoveIndexFromArray';

const useInputStyles = makeStyles(
    createStyles({
        input: {
            marginRight: 40,
        },
    }),
);

const StyledAdornment = styled(InputAdornment).attrs({ position: 'end' })`
    position: absolute;
    right: 0;
    bottom: 21px;
`;

export type ChipFieldWithDropdownListOptions<T> = InputDropdownListOptions<T>;
export type ChipFieldWithDropdownListValue<T> = ChipFieldValueArray<T>;
export type ChipFieldWithDropdownListOnSelectItemHandler<T = any> = InputDropdownListOnSelectItemHandler<T>;
export type ChipFieldWithDropdownListOnRemoveHandler<T = any> = ChipFieldRemoveHandler<T>;

export type ChipFieldWithDropdownListProps<T = any> = {
    ChipFieldProps?: Partial<_ChipFieldProps>;
    InputDropdownProps?: Partial<InputDropdownListProps<T>>;
    autoselect?: boolean;
    error?: _ChipFieldProps['error'];
    fullWidth?: _ChipFieldProps['fullWidth'];
    helperText?: _ChipFieldProps['helperText'];
    hideAfterSelect?: boolean;
    inputValue?: _ChipFieldProps['inputValue'];
    label?: _ChipFieldProps['label'];
    loading?: InputDropdownListProps<T>['loading'];
    margin?: _ChipFieldProps['margin'];
    modifiers?: DSPopperProps['modifiers'];
    noItemsMessage?: InputDropdownListProps<T>['noItemsMessage'];
    onAdd?: _ChipFieldProps['onAdd'];
    onInputChange?: _ChipFieldProps['onInputValueChange'];
    onRemove?: ChipFieldWithDropdownListOnRemoveHandler<T>;
    onSelectItem?: InputDropdownListProps<T>['onSelectItem'];
    options: ChipFieldWithDropdownListOptions<T>;
    placeholder?: _ChipFieldProps['placeholder'];
    value?: ChipFieldWithDropdownListValue<T>;
};

export function ChipFieldWithDropdownList<T>({
    autoselect,
    ChipFieldProps,
    error,
    fullWidth,
    helperText,
    hideAfterSelect,
    InputDropdownProps,
    inputValue,
    label,
    loading,
    margin,
    modifiers,
    noItemsMessage,
    onAdd,
    onInputChange,
    onRemove,
    onSelectItem,
    options,
    placeholder,
    value = [],
}: ChipFieldWithDropdownListProps<T>): JSX.Element {
    const [componentInputValue, setComponentInputValue] = React.useState(inputValue ?? '');

    const [open, setOpen] = React.useState(false);
    const [inputDivRef, setInputDivRef] = React.useState<HTMLElement>();
    const [inputRef, setInputRef] = React.useState<HTMLInputElement>();

    const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

    const inputStyles = useInputStyles();

    const [additionalInputDropdownListProps, forceReRenderDropdown] = InputDropdownList.useForceRerender();

    const controlledInputValue = inputValue ?? componentInputValue;
    const setControlledInputValue = React.useCallback(
        (newValue: string) => {
            onInputChange?.(newValue);
            setComponentInputValue(newValue);
        },
        [onInputChange],
    );

    const handleInputValueChange = React.useCallback<Exclude<_ChipFieldProps['onInputValueChange'], undefined>>(
        newValue => {
            setControlledInputValue(newValue);
            if (autoselect && newValue.trim().length > 0) {
                const foundItemIndex = options.findIndex(item =>
                    (typeof item === 'string' ? item : item.label).toLowerCase().includes(newValue.toLowerCase()),
                );
                if (foundItemIndex > -1) {
                    setSelectedIndex(foundItemIndex);
                    return;
                }
            }
            setSelectedIndex(null);
        },
        [autoselect, options, setControlledInputValue],
    );

    const showDropdown = React.useCallback(() => {
        setOpen(true);
    }, []);

    const hideDropdown = React.useCallback(() => {
        setOpen(false);
    }, []);

    const handleRemove = React.useCallback<ChipFieldRemoveHandler<T>>(
        (item, index, wasBackspace) => {
            if (!(index >= 0 && index < value.length)) {
                return;
            }
            setSelectedIndex(null);
            onRemove?.(item, index, wasBackspace);
        },
        [value.length, onRemove],
    );

    const handleSelectItem = React.useCallback<Exclude<InputDropdownListProps<T>['onSelectItem'], undefined>>(
        (item, index) => {
            if (!(index >= 0 && index < options.length)) {
                return;
            }
            setControlledInputValue('');
            onSelectItem?.(item, index);
            setSelectedIndex(null);
            if (hideAfterSelect) {
                hideDropdown();
            } else {
                inputRef?.focus();
            }
        },
        [options.length, setControlledInputValue, onSelectItem, hideAfterSelect, hideDropdown, inputRef],
    );

    const selectItemAtIndex = React.useCallback(
        (index: number) => {
            if (!(index >= 0 && index < options.length)) {
                return;
            }
            const val = options[index];
            handleSelectItem(typeof val === 'string' ? val : val.value, index);
        },
        [handleSelectItem, options],
    );

    const handleNavigateUp = React.useCallback(() => {
        if (open) {
            setSelectedIndex(oldIndex => {
                if (oldIndex === null) {
                    return options.length - 1;
                } else if (oldIndex <= 0) {
                    return null;
                } else {
                    return oldIndex - 1;
                }
            });
        }
    }, [options.length, open]);

    const handleNavigateDown = React.useCallback(() => {
        if (open) {
            setSelectedIndex(oldIndex => {
                if (oldIndex === null) {
                    return 0;
                } else if (oldIndex >= options.length - 1) {
                    return null;
                } else {
                    return oldIndex + 1;
                }
            });
        }
    }, [options.length, open]);

    const handleKeyDown = React.useCallback<React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>>(
        event => {
            if (open) {
                if (event.key === 'ArrowUp') {
                    handleNavigateUp();
                    event.preventDefault();
                } else if (event.key === 'ArrowDown') {
                    handleNavigateDown();
                    event.preventDefault();
                } else if (event.key === 'Enter') {
                    if (selectedIndex !== null) {
                        selectItemAtIndex(selectedIndex);
                    }
                } else if (event.key === 'Escape' || event.key === 'Tab') {
                    hideDropdown();
                    event.preventDefault();
                }
            } else {
                showDropdown();
            }
        },
        [handleNavigateDown, handleNavigateUp, hideDropdown, open, selectItemAtIndex, selectedIndex, showDropdown],
    );

    const handleHoverItem = React.useCallback<Exclude<InputDropdownListProps<T>['onHoverItem'], undefined>>(
        (_item, index) => {
            setSelectedIndex(index);
        },
        [setSelectedIndex],
    );

    // This nifty little trick forces the dropdown to re-render when the selected items change the width of the input
    React.useEffect(() => {
        forceReRenderDropdown();
    }, [forceReRenderDropdown, value]);

    return (
        <DSClickAwayListener onClickAway={hideDropdown}>
            <span>
                <ChipField
                    {...ChipFieldProps}
                    InputProps={{
                        ...(ChipFieldProps?.InputProps ?? {}),
                        classes: inputStyles,
                        endAdornment: (
                            <StyledAdornment>
                                <IconButton tabIndex="-1" onClick={open ? hideDropdown : showDropdown}>
                                    {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                            </StyledAdornment>
                        ),
                    }}
                    clearOnAdd={false}
                    error={error}
                    fullWidth={fullWidth}
                    helperText={helperText}
                    innerRef={setInputDivRef}
                    inputRef={setInputRef}
                    inputValue={controlledInputValue}
                    label={label}
                    margin={margin}
                    placeholder={placeholder}
                    value={value}
                    onAdd={onAdd}
                    onFocus={showDropdown}
                    onInputValueChange={handleInputValueChange}
                    onKeyDown={handleKeyDown}
                    onRemove={handleRemove}
                />
                <InputDropdownList
                    {...InputDropdownProps}
                    {...additionalInputDropdownListProps}
                    loading={loading}
                    modifiers={modifiers}
                    noItemsMessage={noItemsMessage}
                    open={open}
                    options={options}
                    popperAnchorEl={inputDivRef ?? null}
                    selectedIndex={selectedIndex ?? undefined}
                    onHoverItem={handleHoverItem}
                    onNavigateDown={handleNavigateDown}
                    onNavigateUp={handleNavigateUp}
                    onSelectItem={handleSelectItem}
                />
            </span>
        </DSClickAwayListener>
    );
}
ChipFieldWithDropdownList.displayName = 'ChipFieldWithDropdown';
ChipFieldWithDropdownList.useRemoveIndexFromArray = useRemoveIndexFromArray;
